import React from 'react';
import { Card } from '../ui/Card';
import { Button } from '../ui/Button';
import { ProgressBar } from '../ui/ProgressBar';
import { 
  Calendar, 
  TrendingUp, 
  Share2, 
  ChevronRight, 
  ChevronDown 
} from 'lucide-react';
import { safeFormat } from '../../lib/utils';
import toast from 'react-hot-toast';

interface GoalCardProps {
  goal: any;
  isAdmin: boolean;
  onUpdate: (goal: any) => void;
  isSubGoal?: boolean;
}

export const GoalCard: React.FC<GoalCardProps> = ({ 
  goal, 
  isAdmin, 
  onUpdate, 
  isSubGoal = false 
}) => {
  const percentage = Math.min(Math.round((goal.currentValue / goal.targetValue) * 100), 100);
  const [isExpanded, setIsExpanded] = React.useState(true);

  const copyShareLink = () => {
    const url = `${window.location.origin}/public/goals/${goal.id}`;
    navigator.clipboard.writeText(url);
    toast.success('Public link copied to clipboard');
  };

  return (
    <div className={isSubGoal ? "ml-8 mt-4" : "mb-6 animate-in slide-in-from-left-4 duration-300"}>
      <Card className={isSubGoal ? "p-4 bg-gray-50 border-gray-200 dark:bg-slate-950" : "p-6 overflow-hidden"}>
        <div className="flex items-start justify-between mb-4">
          <div className="flex gap-3">
            {!isSubGoal && goal.subGoals?.length > 0 && (
              <button 
                onClick={() => setIsExpanded(!isExpanded)}
                className="mt-1 text-gray-400 hover:text-gray-600 transition-transform duration-200"
                style={{ transform: isExpanded ? 'rotate(0deg)' : 'rotate(-90deg)' }}
              >
                <ChevronDown size={18} />
              </button>
            )}
            <div>
              <h3 className={isSubGoal ? "text-sm font-semibold text-gray-900" : "text-lg font-semibold text-gray-900"}>
                {goal.title}
              </h3>
              {goal.description && (
                <p className="text-xs text-gray-500 mt-1">{goal.description}</p>
              )}
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            {goal.isPublic && (
              <Button variant="ghost" size="sm" onClick={copyShareLink} title="Share public link">
                <Share2 size={14} />
              </Button>
            )}
            {isAdmin && (
              <Button variant="secondary" size="sm" onClick={() => onUpdate(goal)}>
                Update
              </Button>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="flex items-end justify-between">
            <div>
              <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono mb-1">Progress</p>
              <div className="flex items-baseline gap-1">
                <span className="text-xl font-bold text-navy-900">
                  {goal.unit === '$' ? `$${(goal.currentValue || 0).toLocaleString()}` : `${(goal.currentValue || 0).toLocaleString()} ${goal.unit}`}
                </span>
                <span className="text-xs text-gray-400">
                  of {goal.unit === '$' ? `$${(goal.targetValue || 0).toLocaleString()}` : `${(goal.targetValue || 0).toLocaleString()} ${goal.unit}`}
                </span>
              </div>
            </div>
            <div className="text-right">
              <span className="text-sm font-bold text-blue-700">{percentage}%</span>
            </div>
          </div>

          <ProgressBar 
            value={percentage} 
            variant={percentage >= 100 ? 'teal' : 'purple'} 
            className={isSubGoal ? "h-1.5" : "h-2.5 shadow-inner bg-gray-100 dark:bg-slate-800"}
          />

          <div className="flex flex-wrap items-center justify-between gap-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest font-mono pt-2 border-t border-gray-100/50 mt-4">
            <div className="flex items-center gap-1.5">
              <Calendar size={12} className="text-gray-300" />
              <span>Deadline: {goal.deadline ? safeFormat(goal.deadline, 'MMM d, yyyy') : 'No deadline'}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <TrendingUp size={12} className={percentage >= 100 ? "text-green-500" : "text-blue-500"} />
              <span>{goal.subGoals?.length || 0} Sub-goals</span>
            </div>
          </div>
        </div>
      </Card>

      {isExpanded && goal.subGoals?.map((sub: any) => (
        <GoalCard 
          key={sub.id} 
          goal={sub} 
          isAdmin={isAdmin} 
          onUpdate={onUpdate} 
          isSubGoal 
        />
      ))}
    </div>
  );
};
